/**
 * PRD.218 + PRD.227 — Single shared billing-aggregate hook.
 *
 * Both the Patient Overview billing summary card and the sidebar
 * balance chip MUST consume this hook so they never recompute money
 * math independently. The underlying query returns minor-unit (cents)
 * totals derived from `invoices`, `invoice_payments`, AND
 * `billing_adjustments`. PRD.227 §8.2 formula:
 *   Balance = Charges + Debits − Payments − Credits
 */

import { useMemo } from 'react';
import { usePowerSyncQuery } from '../../adapters/powersync';
import {
  qPatientBillingAggregate,
  qNoOp,
  type PatientBillingAggregateRow,
} from '@helios/data-access';
import { phpFromCents, type Money } from '@helios/domain';

export interface PatientBilling {
  /** Lifetime billed total across the patient's invoices. */
  charged: Money;
  /** Lifetime payments received against the patient's invoices. */
  paid: Money;
  /** PRD.227 — sum of debit adjustments (manual charges that ADD to balance). */
  debit: Money;
  /** PRD.227 — sum of credit adjustments (manual reductions). */
  credit: Money;
  /** charged + debit − paid − credit. Drives both Overview "Remaining" and sidebar chip. */
  balance: Money;
  /** Raw cents copies for SQL-level comparisons (e.g. dialog validation). */
  chargedCents: number;
  paidCents: number;
  debitCents: number;
  creditCents: number;
  balanceCents: number;
  /** Number of invoices that still have positive remaining (excludes status='paid'). */
  unpaidCount: number;
  /** True when any unpaid invoice is > 30 days old. */
  hasOverdue: boolean;
  /** True while the query has no row yet (initial mount). */
  isLoading: boolean;
}

const EMPTY: PatientBillingAggregateRow = {
  charged_cents: 0,
  paid_cents: 0,
  debit_cents: 0,
  credit_cents: 0,
  balance_cents: 0,
  unpaid_count: 0,
  has_overdue: 0,
};

export function usePatientBilling(patientId: string | null | undefined): PatientBilling {
  const { data: rows = [] } = usePowerSyncQuery<PatientBillingAggregateRow>(
    patientId ? qPatientBillingAggregate(patientId) : qNoOp('patient-billing'),
  );

  return useMemo(() => {
    const row = rows[0] ?? EMPTY;
    const chargedCents = row.charged_cents ?? 0;
    const paidCents = row.paid_cents ?? 0;
    const debitCents = row.debit_cents ?? 0;
    const creditCents = row.credit_cents ?? 0;
    const balanceCents = row.balance_cents ?? 0;
    return {
      charged: phpFromCents(chargedCents),
      paid: phpFromCents(paidCents),
      debit: phpFromCents(debitCents),
      credit: phpFromCents(creditCents),
      balance: phpFromCents(balanceCents),
      chargedCents,
      paidCents,
      debitCents,
      creditCents,
      balanceCents,
      unpaidCount: row.unpaid_count ?? 0,
      hasOverdue: (row.has_overdue ?? 0) === 1,
      isLoading: rows.length === 0 && !!patientId,
    };
  }, [rows, patientId]);
}
